import PostalMime, { Address, Attachment as PostalMimeAttachment } from 'postal-mime';
import { Timestamp } from 'firebase/firestore';
import { RcFile } from 'antd/es/upload/interface';
import { Buffer } from 'buffer';
import { msgReaderAttachmentContentType } from '@/services/msgReaderAttachmentContentType';
import {
    ArtifactAttachments,
    AttachmentVersion,
    AttachmentCommunicationRecord,
    ApiCommunicationRecord,
    CommunicationRecord,
    CommunicationTagKey,
    EmailCommunicationRecord,
    LabelId,
    PhoneCallDirection,
    PhoneCallRecord,
    sanitizeEmailBody,
} from '#types';
import { DECORATIVE_IMAGE_SIZE_THRESHOLD } from '#types/System.Parameters.types';
import { communicationTags } from '../components/communicationTags';

/**
 * @returns a deterministic fingerprint for duplicate detection.
 * Prefers Message-ID header (RFC 2822); falls back to from+subject+timestamp+to.
 */
export const getEmailFingerprint = (record: {
    from?: string;
    to?: string[];
    subject?: string;
    timestamp?: { toMillis?: () => number };
    headers?: Record<string, string>;
}): string => {
    if (record.headers) {
        const messageIdKey = Object.keys(record.headers).find(
            (key) => key.toLowerCase() === 'message-id',
        );
        const messageId = messageIdKey ? record.headers[messageIdKey]?.trim() : undefined;
        if (messageId) {
            return `msgid:${messageId}`;
        }
    }

    const from = (record.from || '').trim().toLowerCase();
    const subject = (record.subject || '').trim().toLowerCase();
    const ts = record.timestamp?.toMillis?.() ?? 0;
    const to = [...(record.to || [])].map((t) => t.trim().toLowerCase()).sort().join(',');

    return `content:${from}|${subject}|${ts}|${to}`;
};

/**
 * Pure-parsed email content, no I/O. Shared by CommunicationLog (after upload) and the agent flow.
 */
export interface ParsedEmailContent {
    subject: string;
    from: string;
    to: string[];
    cc?: string[];
    bcc?: string[];
    date: Date;
    bodyText: string;
    headers: Record<string, string>;
    messageId?: string;
    inReplyToId?: string;
    referenceIds?: string[];
    attachments: Array<{
        filename: string;
        contentType: string;
        content: Uint8Array;
    }>;
}

export type ParentUploadFunction = (
    fileToUpload: RcFile,
    emailRecordId: string
) => Promise<{ fileUid: string; versionId: string; versionData: AttachmentVersion } | null>;

const ensureTimestamp = (value?: Timestamp): Timestamp => (
    value instanceof Timestamp ? value : Timestamp.now()
);

/**
 * @returns the string ids a stored assignment holds, or `undefined` for an
 * assignment that survives validation with nothing in it. An empty array and an
 * absent field render alike, but the array persists back through the record
 * replacement path where absence would not, so they collapse here.
 */
const normalizeLabelIds = (value: unknown): LabelId[] | undefined => {
    if (!Array.isArray(value)) return undefined;
    const labelIds = value.filter((id: unknown): id is LabelId => typeof id === 'string');
    return labelIds.length > 0 ? labelIds : undefined;
};

/**
 * Determines whether an email attachment is a decorative image (logo, icon, tracking pixel).
 * Returns true when the MIME type is image/* and its size is below DECORATIVE_IMAGE_SIZE_THRESHOLD.
 */
export const isDecorativeImage = (
    mimeType: string | null | undefined,
    sizeBytes: number,
): boolean => {
    const normalizedMime = (mimeType || '').toLowerCase();
    if (!normalizedMime.startsWith('image/')) return false;
    return sizeBytes < DECORATIVE_IMAGE_SIZE_THRESHOLD;
};

const normalizeEmailRecord = (record: any): EmailCommunicationRecord => {
    const createdAt = ensureTimestamp(record?.createdAt);
    const updatedAt = ensureTimestamp(record?.updatedAt);

    let tag: CommunicationTagKey = 'manual';
    if (record?.tag && communicationTags[record.tag as CommunicationTagKey]) {
        tag = record.tag as CommunicationTagKey;
    }

    return {
        id: record?.id || crypto.randomUUID(),
        key: record?.key || crypto.randomUUID(),
        from: record?.from || '',
        to: Array.isArray(record?.to) ? record.to : [],
        cc: Array.isArray(record?.cc) ? record.cc : undefined,
        bcc: Array.isArray(record?.bcc) ? record.bcc : undefined,
        subject: record?.subject || '',
        body: record?.body,
        bodyAttachment: typeof record?.bodyAttachment?.storagePath === 'string'
            ? record.bodyAttachment
            : undefined,
        timestamp: record?.timestamp instanceof Timestamp
            ? record.timestamp
            : Timestamp.now(),
        hasAttachments: Boolean(
            record?.hasAttachments
            || (record?.attachments && Object.keys(record.attachments).length > 0)
            || (Array.isArray(record?.outboundAttachmentLabels) && record.outboundAttachmentLabels.length > 0),
        ),
        attachments: record?.attachments,
        outboundAttachmentLabels: Array.isArray(record?.outboundAttachmentLabels)
            ? record.outboundAttachmentLabels.filter((x: unknown): x is string => typeof x === 'string')
            : undefined,
        labelIds: normalizeLabelIds(record?.labelIds),
        headers: record?.headers,
        contactEntityRecordId: typeof record?.contactEntityRecordId === 'string'
            ? record.contactEntityRecordId
            : undefined,
        createdAt,
        updatedAt,
        type: 'email',
        tag,
        deleted: record?.deleted === true ? true : undefined,
    };
};

const normalizePhoneRecord = (record: any): PhoneCallRecord => {
    const createdAt = ensureTimestamp(record?.createdAt);
    const updatedAt = ensureTimestamp(record?.updatedAt);

    let tag: CommunicationTagKey = 'manual';
    if (record?.tag && communicationTags[record.tag as CommunicationTagKey]) {
        tag = record.tag as CommunicationTagKey;
    }

    let direction: PhoneCallDirection | undefined;
    if (record?.direction === 'inbound' || record?.direction === 'outbound') {
        direction = record.direction;
    } else if (record?.tag === 'inbound' || record?.tag === 'outbound') {
        direction = record.tag;
    }

    return {
        type: 'phone',
        tag,
        id: record?.id || crypto.randomUUID(),
        key: record?.key || crypto.randomUUID(),
        timestamp: record?.timestamp instanceof Timestamp
            ? record.timestamp
            : Timestamp.now(),
        direction,
        phoneNumber: record?.phoneNumber,
        notes: typeof record?.notes === 'string' ? record.notes : undefined,
        contactEntityRecordId: typeof record?.contactEntityRecordId === 'string'
            ? record.contactEntityRecordId
            : undefined,
        createdAt,
        updatedAt,
        labelIds: normalizeLabelIds(record?.labelIds),
        deleted: record?.deleted === true ? true : undefined,
    };
};

/** Unknown values fall back to `success` for records written before the union
 *  carried `pending`/`duplicate`; every one of those was a completed call. */
const API_OUTCOMES: ApiCommunicationRecord['outcome'][] = ['pending', 'success', 'duplicate', 'error'];

const normalizeApiRecord = (record: any): ApiCommunicationRecord => {
    const createdAt = ensureTimestamp(record?.createdAt);
    const updatedAt = ensureTimestamp(record?.updatedAt);
    const timestamp = ensureTimestamp(record?.timestamp);

    const tag: CommunicationTagKey = record?.tag && communicationTags[record.tag as CommunicationTagKey]
        ? (record.tag as CommunicationTagKey)
        : 'sent';

    const method: 'GET' | 'POST' = record?.method === 'GET' ? 'GET' : 'POST';
    const outcome: ApiCommunicationRecord['outcome'] = API_OUTCOMES.find((o) => o === record?.outcome) ?? 'success';

    return {
        type: 'api',
        tag,
        id: record?.id || crypto.randomUUID(),
        key: record?.key || crypto.randomUUID(),
        timestamp,
        createdAt,
        updatedAt,
        endpoint: typeof record?.endpoint === 'string' ? record.endpoint : '',
        method,
        url: typeof record?.url === 'string' ? record.url : '',
        distributorId: typeof record?.distributorId === 'string' ? record.distributorId : undefined,
        purchaseOrderNumber: typeof record?.purchaseOrderNumber === 'string' ? record.purchaseOrderNumber : undefined,
        externalReference: typeof record?.externalReference === 'string' ? record.externalReference : undefined,
        projectionIncomplete: record?.projectionIncomplete === true ? true : undefined,
        outcome,
        statusMessage: typeof record?.statusMessage === 'string' ? record.statusMessage : undefined,
        salesdocument: typeof record?.salesdocument === 'string' ? record.salesdocument : undefined,
        quoteNumber: typeof record?.quoteNumber === 'string' ? record.quoteNumber : undefined,
        requestAttachmentFileUid: typeof record?.requestAttachmentFileUid === 'string' ? record.requestAttachmentFileUid : undefined,
        responseAttachmentFileUid: typeof record?.responseAttachmentFileUid === 'string' ? record.responseAttachmentFileUid : undefined,
        submissionPdfFileUid: typeof record?.submissionPdfFileUid === 'string' ? record.submissionPdfFileUid : undefined,
        labelIds: normalizeLabelIds(record?.labelIds),
        deleted: record?.deleted === true ? true : undefined,
    };
};

const normalizeAttachmentRecord = (record: any): AttachmentCommunicationRecord | null => {
    const fileUid = typeof record?.fileUid === 'string' ? record.fileUid : '';
    if (!fileUid) {
        return null;
    }
    const createdAt = ensureTimestamp(record?.createdAt);
    const updatedAt = ensureTimestamp(record?.updatedAt);
    const timestamp = ensureTimestamp(record?.timestamp);

    return {
        type: 'attachment',
        id: record?.id || crypto.randomUUID(),
        key: record?.key || crypto.randomUUID(),
        timestamp,
        createdAt,
        updatedAt,
        fileUid,
        previousFileName: typeof record?.previousFileName === 'string' ? record.previousFileName : undefined,
        nextFileName: typeof record?.nextFileName === 'string' ? record.nextFileName : undefined,
        labelIds: normalizeLabelIds(record?.labelIds),
        deleted: record?.deleted === true ? true : undefined,
    };
};

export const migrateToCommunicationLog = (value: unknown): CommunicationRecord[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.map((record) => {
        if (!record || typeof record !== 'object') {
            return null;
        }

        if ((record as any).type === 'phone') {
            return normalizePhoneRecord(record);
        }

        if ((record as any).type === 'api') {
            return normalizeApiRecord(record);
        }

        if ((record as any).type === 'attachment') {
            return normalizeAttachmentRecord(record);
        }

        return normalizeEmailRecord(record);
    }).filter((record): record is CommunicationRecord => Boolean(record));
};

const extractEmlContent = async (
    emlFile: File,
    options?: { sanitize?: boolean },
): Promise<ParsedEmailContent> => {
    const parser = new PostalMime();
    const buffer = await emlFile.arrayBuffer();
    const email = await parser.parse(buffer);

    const getEmailList = (addresses: Address[] | undefined): string[] =>
        (addresses || [])
            .map((addr) => addr.address || '')
            .filter(Boolean);

    const headers: Record<string, string> = {};
    if (email.headers) {
        Object.entries(email.headers).forEach(([key, value]) => {
            if (typeof value === 'object' && value !== null) {
                if ('text' in value && typeof value.text === 'string') {
                    headers[key] = value.text;
                    return;
                }
                if ('value' in value && typeof value.value === 'string') {
                    headers[key] = value.value;
                    return;
                }
                headers[key] = JSON.stringify(value);
                return;
            }
            headers[key] = String(value || '');
        });
    }

    const date = email.date ? new Date(email.date) : new Date();
    const rawBody = email.text?.trim() ? email.text : (email.html || email.text || '');
    const bodyText = options?.sanitize === false ? rawBody : sanitizeEmailBody(rawBody).body;

    const attachments: ParsedEmailContent['attachments'] = [];
    if (email.attachments) {
        for (const a of email.attachments as PostalMimeAttachment[]) {
            if (!a.filename || !a.content) continue;
            const content = a.content instanceof Uint8Array
                ? a.content
                : typeof a.content === 'string'
                    ? new TextEncoder().encode(a.content)
                    : new Uint8Array(a.content);
            attachments.push({
                filename: a.filename,
                contentType: a.mimeType || 'application/octet-stream',
                content,
            });
        }
    }

    return {
        subject: email.subject || '',
        from: email.from?.address || '',
        to: getEmailList(email.to),
        cc: email.cc ? getEmailList(email.cc) : undefined,
        bcc: email.bcc ? getEmailList(email.bcc) : undefined,
        date,
        bodyText,
        headers,
        messageId: email.messageId?.replace(/[<>]/g, ''),
        inReplyToId: email.inReplyTo?.replace(/[<>]/g, ''),
        referenceIds: email.references?.split(/\s+/).filter(Boolean).map((r) => r.replace(/[<>]/g, '')),
        attachments,
    };
};

const uploadParsedEmailAttachments = async (
    parsedAttachments: ParsedEmailContent['attachments'],
    uploadAttachmentToParent: ParentUploadFunction,
    emailRecordId: string,
): Promise<ArtifactAttachments> => {
    const result: ArtifactAttachments = {};
    for (const att of parsedAttachments) {
        const sizeBytes = att.content.byteLength;
        if (isDecorativeImage(att.contentType, sizeBytes)) continue;
        try {
            const sliced = (att.content.buffer as ArrayBuffer).slice(
                att.content.byteOffset,
                att.content.byteOffset + att.content.byteLength,
            );
            const attachmentFile = new File(
                [sliced],
                att.filename,
                { type: att.contentType },
            ) as RcFile;
            attachmentFile.uid = crypto.randomUUID();

            const uploadResult = await uploadAttachmentToParent(attachmentFile, emailRecordId);
            if (!uploadResult) {
                console.warn(`Failed to upload embedded attachment: ${att.filename}`);
                continue;
            }
            const { fileUid, versionId, versionData } = uploadResult;
            if (!result[fileUid]) {
                result[fileUid] = {
                    fileName: versionData.versionFileName,
                    currentVersionId: versionId,
                    versions: {},
                };
            } else {
                result[fileUid].fileName = versionData.versionFileName;
                result[fileUid].currentVersionId = versionId;
            }
            result[fileUid].versions[versionId] = versionData;
        } catch (uploadError) {
            console.error(`Error processing/uploading embedded attachment ${att.filename}:`, uploadError);
        }
    }
    return result;
};

/**
 * Parses an .eml file and extracts email data and attachments
 * @param preGeneratedEmailRecordId Optional pre-generated ID to link the email record with its attachment
 */
export const parseEML = async (
    emlFile: File,
    uploadAttachmentToParent: ParentUploadFunction,
    parentDocId: string,
    preGeneratedEmailRecordId?: string
): Promise<EmailCommunicationRecord> => {
    const parsed = await extractEmlContent(emlFile);
    const now = Timestamp.now();
    const timestampSuffix = parsed.date.toISOString();
    const fromAddressSuffix = (parsed.from || 'unknown_from').replace(/[^a-zA-Z0-9_.-]/g, '').slice(0, 50);
    const subjectSuffix = (parsed.subject || 'no_subject').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 50);
    const uniqueEmailSuffix = `${timestampSuffix}_${fromAddressSuffix}_${subjectSuffix}`;
    const emailRecordId = preGeneratedEmailRecordId || `${parentDocId}#EMAIL#${uniqueEmailSuffix}#${crypto.randomUUID()}`;

    const parsedEmailAttachments = await uploadParsedEmailAttachments(
        parsed.attachments,
        uploadAttachmentToParent,
        emailRecordId,
    );

    return {
        type: 'email',
        tag: 'drag',
        id: emailRecordId,
        key: `${emailRecordId}#${crypto.randomUUID()}`,
        from: parsed.from,
        to: parsed.to,
        cc: parsed.cc ?? [],
        bcc: parsed.bcc ?? [],
        subject: parsed.subject,
        body: parsed.bodyText,
        timestamp: Timestamp.fromDate(parsed.date),
        hasAttachments: Object.keys(parsedEmailAttachments).length > 0,
        attachments: parsedEmailAttachments,
        headers: parsed.headers,
        createdAt: now,
        updatedAt: now,
    };
};

type ParsedMsgAttachment = {
    fileName?: string;
    fileNameShort?: string;
    mimeType?: string;
    contentType?: string;
    /** Present on list entries from @kenjiuno/msgreader when getAttachment lacks mime fields */
    attachMimeTag?: string;
    contentId?: string;
    isInline?: boolean;
    inline?: boolean;
};

type ParsedMsgRecipient = {
    name?: string;
    email?: string;
    emailAddress?: string;
    smtpAddress?: string;
    recipientType?: number | string;
    type?: number | string;
};

type ParsedMsgData = {
    subject?: string;
    senderName?: string;
    senderEmail?: string;
    senderEmailAddress?: string;
    senderSmtpAddress?: string;
    sentRepresentingSmtpAddress?: string;
    creatorSMTPAddress?: string;
    lastModifierSMTPAddress?: string;
    from?: string;
    body?: string;
    bodyHtml?: string;
    html?: Uint8Array;
    internetCodepage?: number;
    messageCodepage?: number;
    recipients?: ParsedMsgRecipient[];
    attachments?: ParsedMsgAttachment[];
    messageDeliveryTime?: string | number | Date;
    clientSubmitTime?: string | number | Date;
    date?: string | number | Date;
};

const extractMsgContent = async (
    msgFile: File,
    options?: { sanitize?: boolean },
): Promise<ParsedEmailContent> => {
    const globalAny = globalThis as unknown as { Buffer?: typeof Buffer };
    if (!globalAny.Buffer) {
        globalAny.Buffer = Buffer;
    }

    const mod = await import('@kenjiuno/msgreader');
    const MsgReaderCtor = (mod.default as any)?.default ?? mod.default;
    const msgBuffer = await msgFile.arrayBuffer();
    const msgReader = new MsgReaderCtor(new Uint8Array(msgBuffer));
    const msgData = msgReader.getFileData() as ParsedMsgData;

    const headers: Record<string, string> = {};

    const normalizeText = (value: string): string => value.trim().replace(/\s+/g, ' ');

    const isExchangeX500 = (value: string): boolean => {
        const trimmed = value.trim();
        if (!trimmed) {
            return false;
        }
        if (trimmed.startsWith('/')) {
            return /^\/o=/i.test(trimmed) || /\/ou=|\/cn=/i.test(trimmed);
        }
        return false;
    };

    const extractEmailAddress = (value: string): string => {
        const match = value.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
        return match ? match[0] : '';
    };

    const getSenderNameCandidate = (): string => {
        if (typeof msgData.senderName === 'string' && msgData.senderName.trim()) {
            const name = normalizeText(msgData.senderName);
            if (!isExchangeX500(name)) {
                return name;
            }
        }
        const fromText = typeof msgData.from === 'string' ? msgData.from.trim() : '';
        if (!fromText || isExchangeX500(fromText)) {
            return '';
        }
        const angleBracketIdx = fromText.indexOf('<');
        if (angleBracketIdx > 0) {
            const name = normalizeText(fromText.slice(0, angleBracketIdx));
            return name;
        }
        return '';
    };

    const getSenderEmailCandidate = (): string => {
        const candidates = [
            typeof msgData.senderSmtpAddress === 'string' ? msgData.senderSmtpAddress : '',
            typeof msgData.senderEmailAddress === 'string' ? msgData.senderEmailAddress : '',
            typeof msgData.sentRepresentingSmtpAddress === 'string' ? msgData.sentRepresentingSmtpAddress : '',
            typeof msgData.lastModifierSMTPAddress === 'string' ? msgData.lastModifierSMTPAddress : '',
            typeof msgData.creatorSMTPAddress === 'string' ? msgData.creatorSMTPAddress : '',
            typeof msgData.senderEmail === 'string' ? msgData.senderEmail : '',
            typeof msgData.from === 'string' ? msgData.from : '',
        ].filter(Boolean) as string[];

        for (const candidate of candidates) {
            if (isExchangeX500(candidate)) {
                continue;
            }
            const email = extractEmailAddress(candidate);
            if (email) {
                return email;
            }
        }
        return '';
    };

    const emailDateCandidate = msgData.messageDeliveryTime ?? msgData.clientSubmitTime ?? msgData.date;
    const emailDate = (() => {
        const candidate = emailDateCandidate ? new Date(emailDateCandidate as any) : new Date();
        return Number.isNaN(candidate.getTime()) ? new Date() : candidate;
    })();

    const senderEmail = getSenderEmailCandidate();
    const senderName = getSenderNameCandidate();
    const from = senderEmail
        ? (senderName ? `${senderName} (${senderEmail})` : senderEmail)
        : senderName;

    const recipients = Array.isArray(msgData.recipients) ? msgData.recipients : [];
    const to: string[] = [];
    const cc: string[] = [];
    const bcc: string[] = [];

    const formatRecipient = (recipient: ParsedMsgRecipient): string => {
        const rawEmail = (
            (typeof recipient?.email === 'string' && recipient.email)
            || (typeof recipient?.emailAddress === 'string' && recipient.emailAddress)
            || ''
        );

        const smtpAddr = typeof recipient?.smtpAddress === 'string' ? recipient.smtpAddress.trim() : '';
        const recipientName = typeof recipient?.name === 'string' ? normalizeText(recipient.name) : '';

        let emailAddress = '';
        if (smtpAddr && !isExchangeX500(smtpAddr)) {
            emailAddress = extractEmailAddress(smtpAddr) || smtpAddr;
        } else if (rawEmail && !isExchangeX500(rawEmail)) {
            emailAddress = extractEmailAddress(rawEmail) || rawEmail;
        }

        const cleanName = recipientName && !isExchangeX500(recipientName) ? recipientName : '';

        if (emailAddress && cleanName && cleanName.toLowerCase() !== emailAddress.toLowerCase()) {
            return `${cleanName} (${emailAddress})`;
        }
        return emailAddress || cleanName;
    };

    recipients.forEach((recipient) => {
        const formatted = formatRecipient(recipient);
        if (!formatted) {
            return;
        }

        const rawType = recipient?.recipientType ?? recipient?.type;
        const typeString = typeof rawType === 'string' ? rawType.toLowerCase() : null;

        if (rawType === 2 || typeString === 'cc') {
            cc.push(formatted);
            return;
        }
        if (rawType === 3 || typeString === 'bcc') {
            bcc.push(formatted);
            return;
        }
        to.push(formatted);
    });

    const codepageToEncoding = (cp?: number): string => {
        if (cp === 65001) return 'utf-8';
        if (cp === 1252) return 'windows-1252';
        if (cp === 1250) return 'windows-1250';
        if (cp === 1251) return 'windows-1251';
        if (cp === 28591) return 'iso-8859-1';
        return 'utf-8';
    };
    const decodeHtmlBytes = (bytes: Uint8Array): string => {
        const enc = codepageToEncoding(msgData.internetCodepage ?? msgData.messageCodepage);
        try {
            return new TextDecoder(enc).decode(bytes);
        } catch {
            return new TextDecoder('utf-8').decode(bytes);
        }
    };

    const bodyPlain = typeof msgData.body === 'string' ? msgData.body : '';
    const bodyHtml = typeof msgData.bodyHtml === 'string'
        ? msgData.bodyHtml
        : (msgData.html instanceof Uint8Array ? decodeHtmlBytes(msgData.html) : '');
    const rawBody = bodyPlain.trim() ? bodyPlain : (bodyHtml || bodyPlain || '');

    const bodyText = options?.sanitize === false ? rawBody : sanitizeEmailBody(rawBody).body;

    const rawHeaders = typeof (msgData as { headers?: unknown }).headers === 'string'
        ? (msgData as { headers: string }).headers
        : '';
    const messageId = rawHeaders.match(/^Message-ID:\s*<?([^>\s]+)>?/im)?.[1];
    const inReplyToId = rawHeaders.match(/^In-Reply-To:\s*<?([^>\s]+)>?/im)?.[1];
    const referencesRaw = rawHeaders.match(/^References:\s*(.+)/im)?.[1];
    const referenceIds = referencesRaw?.split(/\s+/).filter(Boolean).map((r) => r.replace(/[<>]/g, ''));

    const parsedAttachments: ParsedEmailContent['attachments'] = [];
    const rawAttachments = Array.isArray(msgData.attachments) ? msgData.attachments : [];
    for (const msgAttachment of rawAttachments) {
        const attachmentData = typeof msgReader.getAttachment === 'function'
            ? msgReader.getAttachment(msgAttachment as any)
            : null;

        const fileName = (
            (attachmentData && typeof attachmentData.fileName === 'string' && attachmentData.fileName)
            || msgAttachment?.fileName
            || msgAttachment?.fileNameShort
            || ''
        );
        const content = attachmentData?.content;
        if (!fileName || !content) continue;

        const contentType = msgReaderAttachmentContentType(attachmentData, msgAttachment);
        const bytes = content instanceof Uint8Array ? content : new Uint8Array(content);
        parsedAttachments.push({ filename: fileName, contentType, content: bytes });
    }

    return {
        subject: msgData.subject || '',
        from,
        to,
        cc: cc.length ? cc : undefined,
        bcc: bcc.length ? bcc : undefined,
        date: emailDate,
        bodyText,
        headers,
        messageId,
        inReplyToId,
        referenceIds,
        attachments: parsedAttachments,
    };
};

/**
 * Parses an Outlook .msg file and extracts email data and attachments.
 * @param preGeneratedEmailRecordId Optional pre-generated ID to link the email record with its attachment
 */
export const parseMSG = async (
    msgFile: File,
    uploadAttachmentToParent: ParentUploadFunction,
    parentDocId: string,
    preGeneratedEmailRecordId?: string
): Promise<EmailCommunicationRecord> => {
    const parsed = await extractMsgContent(msgFile);
    const now = Timestamp.now();
    const fromIdSeed = parsed.from || 'unknown_from';
    const timestampSuffix = parsed.date.toISOString();
    const fromAddressSuffix = fromIdSeed.replace(/[^a-zA-Z0-9_.-]/g, '').slice(0, 50);
    const subjectSuffix = (parsed.subject || 'no_subject').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 50);
    const uniqueEmailSuffix = `${timestampSuffix}_${fromAddressSuffix}_${subjectSuffix}`;
    const emailRecordId = preGeneratedEmailRecordId || `${parentDocId}#EMAIL#${uniqueEmailSuffix}#${crypto.randomUUID()}`;

    const parsedEmailAttachments = await uploadParsedEmailAttachments(
        parsed.attachments,
        uploadAttachmentToParent,
        emailRecordId,
    );

    return {
        type: 'email',
        tag: 'drag',
        id: emailRecordId,
        key: `${emailRecordId}#${crypto.randomUUID()}`,
        from: parsed.from,
        to: parsed.to,
        cc: parsed.cc,
        bcc: parsed.bcc,
        subject: parsed.subject,
        body: parsed.bodyText,
        timestamp: Timestamp.fromDate(parsed.date),
        hasAttachments: Object.keys(parsedEmailAttachments).length > 0,
        attachments: parsedEmailAttachments,
        headers: parsed.headers,
        createdAt: now,
        updatedAt: now,
    };
};

/**
 * Pure email parser: extracts subject, body, attachments etc. without performing any I/O.
 * Used by both CommunicationLog (via the parseEmailFile wrapper) and the agent flow.
 *
 * Set `options.sanitize` to `false` to skip body sanitization (HTML/base64 stripping
 * and truncation). Defaults to `true`.
 */
export const parseEmailContent = async (
    file: File,
    options?: { sanitize?: boolean },
): Promise<ParsedEmailContent> => {
    const fileName = (file.name || '').toLowerCase();
    if (fileName.endsWith('.eml')) return extractEmlContent(file, options);
    if (fileName.endsWith('.msg')) return extractMsgContent(file, options);
    throw new Error('Unsupported email file type. Please upload a .eml or .msg file.');
};

/**
 * Parses a .eml or .msg email file, uploading embedded attachments and filtering decorative inline images.
 */
export const parseEmailFile = async (
    emailFile: File,
    uploadAttachmentToParent: ParentUploadFunction,
    parentDocId: string,
    preGeneratedEmailRecordId?: string
): Promise<EmailCommunicationRecord> => {
    const fileName = (emailFile.name || '').toLowerCase();
    if (fileName.endsWith('.eml')) {
        return parseEML(emailFile, uploadAttachmentToParent, parentDocId, preGeneratedEmailRecordId);
    }
    if (fileName.endsWith('.msg')) {
        return parseMSG(emailFile, uploadAttachmentToParent, parentDocId, preGeneratedEmailRecordId);
    }
    throw new Error('Unsupported email file type. Please upload a .eml or .msg file.');
};
